import React, { useState, useEffect, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import GraphService_DN from '../../services/GraphService_DN';
import { getYears } from '../../interfaces/traffic_accidents/FormControllerData';
import SelectionData from '../../interfaces/Graphs/SelectionData';
import { MdLocalPolice } from "react-icons/md";
import './GraphStyles.css';

interface ChartData {
    value: number;
    label: string;
    count: number;
}

interface WeeklyChartProps {
    isVisible: boolean;
}

export default function WeeklyChart({ isVisible }: WeeklyChartProps) {
    const { t, i18n } = useTranslation("traffic");
    const [data, setData] = useState<ChartData[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const [selectedYear, setSelectedYear] = useState<string>('all');
    const [selectedIndicator, setSelectedIndicator] = useState<string>('dayOfWeek');
    
    const getAvailableYears = useCallback(() => {
        return [
            { Code: 'all', Type: '\u00A0\u00A0\u00A0\u00A0\u00A0' },
            ...getYears().filter(year => year.Code !== '2016')
        ];
    }, [selectedIndicator]);
    
    const [years, setYears] = useState<SelectionData[]>(getAvailableYears());
    
    const indicators = [
        { code: 'dayOfWeek', name: t('Traffic.indicators.dayOfWeek'), xAxisLabel: t('Traffic.labels.dayOfWeek') },
        { code: 'month', name: t('Traffic.indicators.month'), xAxisLabel: t('Traffic.labels.month') },
        { code: 'hour', name: t('Traffic.indicators.hour'), xAxisLabel: t('Traffic.labels.hour') }
    ];

    console.log('WeeklyChart render - isVisible:', isVisible, 'loading:', loading, 'data length:', data.length);

    const getDayLabels = () => [
        t('Traffic.days.sunday'),
        t('Traffic.days.monday'),
        t('Traffic.days.tuesday'),
        t('Traffic.days.wednesday'),
        t('Traffic.days.thursday'),
        t('Traffic.days.friday'),
        t('Traffic.days.saturday')
    ];
    
    const getMonthLabels = () => [
        t('Traffic.months.january'), t('Traffic.months.february'), t('Traffic.months.march'), t('Traffic.months.april'),
        t('Traffic.months.may'), t('Traffic.months.june'), t('Traffic.months.july'), t('Traffic.months.august'),
        t('Traffic.months.september'), t('Traffic.months.october'), t('Traffic.months.november'), t('Traffic.months.december')
    ];
    
    const getHourLabels = () => Array.from({length: 24}, (_, i) => `${i}:00`);
    
    const transformDayData = (apiData: any[]): ChartData[] => {
        const dayNames = getDayLabels();
        return apiData
            .filter(item => item && typeof item === 'object')
            .map(item => {
                const backendDay = item.label || item.Label || item.LABEL;
                const count = item.value || item.Value || item.VALUE;
                
                let frontendDay;
                if (backendDay === 7) {
                    frontendDay = 0; // Nedeľa: Backend 7 -> Frontend 0
                } else if (backendDay >= 1 && backendDay <= 6) {
                    frontendDay = backendDay; // Pondelok-Sobota: Backend 1-6 -> Frontend 1-6  
                } else {
                    frontendDay = 0; 
                }
                
                return {
                    value: frontendDay,
                    label: dayNames[frontendDay] || `Deň ${frontendDay}`,
                    count: count || 0
                };
            })
            .filter((item: ChartData) => 
                item.value >= 0 && item.value <= 6 && 
                typeof item.count === 'number' && item.count >= 0
            )
            .sort((a: ChartData, b: ChartData) => {
                // Custom sort: Monday(1) first, then Tue(2)...Sat(6), then Sunday(0) last
                if (a.value === 0) return 1; // Sunday goes last
                if (b.value === 0) return -1; // Sunday goes last
                return a.value - b.value;
            });
    };
    
    const transformMonthData = (apiData: any[]): ChartData[] => {
        const monthNames = getMonthLabels();
        return apiData
            .filter(item => item && typeof item === 'object')
            .map(item => {
                const month = item.label || item.Label || item.LABEL;
                const count = item.value || item.Value || item.VALUE;
                
                return {
                    // Convert 1-12 to 0-11 for array indexing
                    value: month - 1, 
                    label: monthNames[month - 1] || `Mesiac ${month}`,
                    count: count || 0
                };
            })
            .filter((item: ChartData) => 
                item.value >= 0 && item.value <= 11 && 
                typeof item.count === 'number' && item.count >= 0
            )
            .sort((a: ChartData, b: ChartData) => a.value - b.value);
    };
    
    const transformHourData = (apiData: any[]): ChartData[] => {
        const hourLabels = getHourLabels();
        return apiData
            .filter(item => item && typeof item === 'object')
            .map(item => {
                const hour = item.label || item.Label || item.LABEL;
                const count = item.value || item.Value || item.VALUE;

                return {
                    value: hour,
                    label: hourLabels[hour] || `${hour}:00`,
                    count: count || 0
                };
            })
            .filter((item: ChartData) => 
                item.value >= 0 && item.value <= 23 && 
                typeof item.count === 'number' && item.count >= 0
            )
            .sort((a: ChartData, b: ChartData) => a.value - b.value);
    };

    // Fetch data for weekly chart
    const fetchWeeklyData = useCallback(async () => {
        console.log('fetchWeeklyData called with indicator:', selectedIndicator, 'year:', selectedYear);
        setLoading(true);
        setError("");
        
        try {
            let apiType: string;
            let transformFunction: (data: any[]) => ChartData[];
            switch (selectedIndicator) {
                case 'dayOfWeek':
                    apiType = 'week';
                    transformFunction = transformDayData;
                    break;
                case 'month':
                    apiType = 'month';
                    transformFunction = transformMonthData;
                    break;
                case 'hour':
                    apiType = 'hour';
                    transformFunction = transformHourData;
                    break;
                default:
                    setError(`Neznámy ukazovateľ: ${selectedIndicator}`);
                    setData([]);
                    setLoading(false);
                    return;
            }
            const apiUrl = `/Graph/popular-animals/${apiType}/${selectedYear}`;
            const response = await GraphService_DN.FetchDataFrom_API(apiUrl);
            const apiData = response.data;
            console.log('API response .data:', apiData);
            let dataArray = null;
            if (Array.isArray(apiData)) {
                dataArray = apiData;
            } else if (apiData?.data && Array.isArray(apiData.data)) {
                dataArray = apiData.data;
            } else {
                console.error('Invalid response format:', apiData);
                setError("Invalid response format from API");
                return;
            }
            if (dataArray && dataArray.length > 0) {
                const transformedData = transformFunction(dataArray);
                console.log('Final transformed data:', transformedData);
                setData(transformedData);
            } else {
                console.warn('No data in dataArray');
                setError("No data returned from API");
                setData([]);
            }
        } catch (error) {
            console.error('API call failed:', error);
            setError(`Error loading data: ${error instanceof Error ? error.message : 'Unknown error'}`);
            setData([]);
        } finally {
            setLoading(false);
            console.log('fetchWeeklyData finished, loading set to false');
        }
    }, [selectedYear, selectedIndicator]);

    const handleYearChange = (event: any) => {
        setSelectedYear(event.target.value);
        setData([]);
    };
    
    const handleIndicatorChange = (event: any) => {
        const newIndicator = event.target.value;
        setSelectedIndicator(newIndicator);
        setData([]);
        
        if (selectedYear !== 'all' && selectedYear !== '2024' && selectedYear !== '2023') {
            setSelectedYear('all');
        }
    };

    // Update years when indicator changes
    useEffect(() => {
        const newYears = getAvailableYears();
        setYears(newYears);
    }, [selectedIndicator]);

    // Fetch data when component becomes visible
    useEffect(() => {
        if (isVisible) {
            fetchWeeklyData();
        } else {
            console.log('WeeklyChart not visible, skipping fetch');
        }
    }, [isVisible, fetchWeeklyData]);

    // Update labels when language changes without refetching data
    useEffect(() => {
        if (data.length > 0) {
            if (selectedIndicator === 'dayOfWeek') {
                const dayNames = getDayLabels();
                const updatedData = data.map(item => ({
                    ...item,
                    label: dayNames[item.value] || `Deň ${item.value}`
                }));
                setData(updatedData);
            } else if (selectedIndicator === 'month') {
                const monthNames = getMonthLabels();
                const updatedData = data.map(item => ({
                    ...item,
                    label: monthNames[item.value] || `Mesiac ${item.value + 1}`
                }));
                setData(updatedData);
            }
        }
    }, [i18n.language]);

    const getTitleAndLabels = () => {
        const currentIndicator = indicators.find(ind => ind.code === selectedIndicator);
        if (currentIndicator) {
            return {
                title: t('Traffic.chartTitle', { label: currentIndicator.xAxisLabel }),
                xAxisLabel: currentIndicator.xAxisLabel,
                yAxisLabel: t('Traffic.labels.yAxis')
            };
        }
        return {
            title: t('Traffic.chartTitleFallback'),
            xAxisLabel: t('Traffic.labels.category'),
            yAxisLabel: t('Traffic.labels.yAxis')
        };
    };

    const getChartOption = () => {
        if (!data.length) {
            console.log('No data available for chart');
            return {};
        }

        const { title, xAxisLabel, yAxisLabel } = getTitleAndLabels();

        return {
            title: {
                text: title,
                left: 'center',
                textStyle: {
                    fontSize: 16,
                    fontWeight: 'bold'
                }
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow'
                },
                formatter: (params: any) => {
                    const data = params[0];
                    return `${data.name}<br/>${t('Traffic.tooltipAccidents')}: ${data.value}`;
                }
            },
            toolbox: {
                show: true,
                orient: "vertical",
                left: "right",
                top: "center",
                feature: {
                    mark: { show: true },
                    saveAsImage: { show: true },
                },
            },
            grid: {
                left: '10%',
                right: '10%',
                bottom: selectedIndicator === 'hour' ? '20%' : '15%', // More space for hour labels
                top: '20%'
            },
            xAxis: {
                type: 'category',
                data: data.map(item => item.label),
                axisLabel: {
                    interval: 0,
                    rotate: selectedIndicator === 'hour' ? 45 : 0, // Rotate labels for hours to fit better
                    fontSize: 12
                },
                name: xAxisLabel,
                nameLocation: 'middle',
                nameGap: selectedIndicator === 'hour' ? 60 : 35, // More space for rotated labels
                nameTextStyle: {
                    fontSize: 14,
                    fontWeight: 'bold'
                }
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    formatter: '{value}'
                },
                name: yAxisLabel,
                nameLocation: 'middle',
                nameGap: 50,
                nameTextStyle: {
                    fontSize: 14,
                    fontWeight: 'bold'
                }
            },
            series: [
                {
                    name: t('Traffic.tooltipAccidents'),
                    type: 'bar',
                    data: data.map(item => item.count),
                    itemStyle: {
                        color: '#4285f4',
                        borderRadius: [4, 4, 0, 0]
                    },
                    label: {
                        show: true,
                        position: 'top',
                        formatter: '{c}',
                        fontSize: 12,
                        fontWeight: 'bold',
                        color: '#333'
                    }
                }
            ]
        };
    };

    if (!isVisible) return null;

    return (
        <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
            <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                alignItems: 'center', 
                marginBottom: '20px' 
            }}>
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>{t('Traffic.indicator')}</InputLabel>
                        <Select
                            value={selectedIndicator}
                            onChange={handleIndicatorChange}
                            label={t('Traffic.indicator')}
                        >
                            {indicators.map((indicator) => (
                                <MenuItem key={indicator.code} value={indicator.code}>
                                    {indicator.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel>{t('Traffic.year')}</InputLabel>
                        <Select
                            value={selectedYear}
                            onChange={handleYearChange}
                            label={t('Traffic.year')}
                        >
                            {years.map((year) => (
                                <MenuItem key={year.Code} value={year.Code}>
                                    {year.Type}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
            </div>
            
            <div className="graph_wrapper">
                { loading && <div className="loading">
                    <div><MdLocalPolice /> {t('Traffic.loading')}</div>
                </div> }
            
            {error ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'red' }}>
                        {t('Traffic.error', { error })}
                    </div>
                ) : data.length > 0 ? (
                    <ReactECharts 
                        option={getChartOption()}
                        style={{ height: '400px', width: '100%' }}
                        notMerge={true}
                    />
                ) : (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        {t('Traffic.noData')}
                    </div>
                )}
            </div>
        </div>
    );
}